import React, { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  align?: "left" | "center";
  actionLabel?: string;
  actionTo?: string;
}

/**
 * Consistent section heading used across the homepage:
 * eyebrow label + title + optional description + optional "view all" link.
 * Note: intentionally does NOT use the global `.section-title` class so it can
 * control its own spacing without inheriting that class's bottom margin.
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  description,
  align = "left",
  actionLabel,
  actionTo,
}) => {
  const centered = align === "center";

  const containerClass = centered
    ? "flex flex-col items-center text-center mb-10 md:mb-14"
    : "flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-10 md:mb-14";

  return (
    <div className={containerClass}>
      <div className={`max-w-2xl ${centered ? "mx-auto" : ""}`}>
        <span className={`eyebrow ${centered ? "justify-center" : ""}`}>{eyebrow}</span>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
          {title}
        </h2>
        {description && (
          <p className="mt-4 text-base md:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>

      {actionLabel && actionTo && (
        <Link
          to={actionTo}
          className="group inline-flex items-center gap-2 text-sm font-semibold text-primary-600 dark:text-primary-400 shrink-0"
        >
          {actionLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      )}
    </div>
  );
};

export default SectionHeader;
